import React, { useState } from 'react';
import { Briefcase, MapPin, Clock, ArrowRight, Sparkles, Users, Heart } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { PageHero } from './AboutPage';

const jobs = [
  { title: 'CBG Plant Manager', dept: 'Operations', location: 'Lucknow, UP', type: 'Full-time' },
  { title: 'Senior Solar Design Engineer', dept: 'Engineering', location: 'Pune, MH', type: 'Full-time' },
  { title: 'EV Charging Network Lead', dept: 'Mobility', location: 'Bangalore, KA', type: 'Full-time' },
  { title: 'Business Development — South India', dept: 'Sales', location: 'Chennai, TN', type: 'Full-time' },
  { title: 'Process Chemist (Bio-Diesel)', dept: 'R&D', location: 'Vadodara, GJ', type: 'Full-time' },
  { title: 'Field Service Technician', dept: 'Service', location: 'Multiple', type: 'Full-time' },
  { title: 'Sustainability Communications Intern', dept: 'Marketing', location: 'Noida, UP', type: 'Internship' },
];

const perks = [
  { icon: Sparkles, title: 'Mission-driven work', desc: 'Build energy that powers a sustainable Bharat.' },
  { icon: Users, title: 'World-class team', desc: 'Learn from leaders in renewable energy.' },
  { icon: Heart, title: 'Wellness & growth', desc: 'Comprehensive insurance, training stipends, and ESOPs.' },
];

const CareerPage = () => {
  const { toast } = useToast();
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', phone: '' });

  const apply = (e) => {
    e.preventDefault();
    const saved = JSON.parse(localStorage.getItem('ie_applications') || '[]');
    saved.push({ ...form, job: selected, at: new Date().toISOString() });
    localStorage.setItem('ie_applications', JSON.stringify(saved));
    toast({ title: 'Application sent!', description: `Thanks for applying for ${selected}. We'll be in touch.` });
    setSelected(null);
    setForm({ name: '', email: '', phone: '' });
  };

  return (
    <div>
      <PageHero title="Careers" subtitle="Join the team building India's clean energy future." />

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-3 gap-6">
          {perks.map((p) => (
            <div key={p.title} className="bg-emerald-50/40 border border-emerald-100 rounded-3xl p-8">
              <div className="w-12 h-12 rounded-2xl bg-emerald-500 grid place-items-center"><p.icon className="w-6 h-6 text-white" /></div>
              <h3 className="mt-5 text-xl font-extrabold text-[#0b1a1a]">{p.title}</h3>
              <p className="mt-2 text-slate-600">{p.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="py-20 bg-gradient-to-b from-white to-emerald-50/30">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center">
            <h2 className="text-4xl md:text-5xl font-extrabold text-[#0b1a1a]">Open <span className="text-emerald-600">Positions</span></h2>
            <p className="mt-3 text-slate-600">Find a role where your skills fuel the green transition.</p>
          </div>
          <div className="mt-12 space-y-4">
            {jobs.map((j) => (
              <div key={j.title} className="bg-white border border-slate-100 hover:border-emerald-200 rounded-2xl p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4 hover:shadow-lg hover:shadow-emerald-100 transition-all">
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-xl bg-emerald-50 grid place-items-center"><Briefcase className="w-5 h-5 text-emerald-600" /></div>
                  <div>
                    <div className="font-extrabold text-[#0b1a1a]">{j.title}</div>
                    <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-slate-500 font-semibold">
                      <span>{j.dept}</span>
                      <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{j.location}</span>
                      <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{j.type}</span>
                    </div>
                  </div>
                </div>
                <button onClick={() => setSelected(j.title)} className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white px-5 py-2.5 rounded-full font-semibold text-sm transition-colors">
                  Apply Now <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {selected && (
        <div className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm grid place-items-center p-6" onClick={() => setSelected(null)}>
          <div className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="text-xs font-bold uppercase tracking-widest text-emerald-600">Apply for</div>
            <h3 className="mt-1 text-2xl font-extrabold text-[#0b1a1a]">{selected}</h3>
            <form onSubmit={apply} className="mt-6 space-y-4">
              <input required value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})} placeholder="Full name" className="w-full px-4 py-3 rounded-xl bg-slate-50 border border-slate-200 focus:border-emerald-400 focus:bg-white outline-none focus:ring-2 focus:ring-emerald-100 font-medium" />
              <input required type="email" value={form.email} onChange={(e)=>setForm({...form,email:e.target.value})} placeholder="Email" className="w-full px-4 py-3 rounded-xl bg-slate-50 border border-slate-200 focus:border-emerald-400 focus:bg-white outline-none focus:ring-2 focus:ring-emerald-100 font-medium" />
              <input required value={form.phone} onChange={(e)=>setForm({...form,phone:e.target.value})} placeholder="Phone" className="w-full px-4 py-3 rounded-xl bg-slate-50 border border-slate-200 focus:border-emerald-400 focus:bg-white outline-none focus:ring-2 focus:ring-emerald-100 font-medium" />
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setSelected(null)} className="flex-1 px-5 py-3 rounded-full font-semibold border border-slate-200 hover:bg-slate-50">Cancel</button>
                <button type="submit" className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white px-5 py-3 rounded-full font-semibold shadow-md">Submit</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CareerPage;
